import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, ZoomIn, X } from "lucide-react";
import { SERVICES } from "@/site.config";

// Map service id (from site.config.js) → SEO page slug
const SERVICE_SLUG_MAP = {
  gouttieres: "nettoyage-gouttieres-martinique",
  facades: "lessivage-sols-martinique",
  photoluminescent: "nez-de-marches-photoluminescents-martinique",
  "plots-solaires": "plots-solaires-led-martinique",
  depoussierage: "depoussierage-hauteur-martinique",
};

export default function Services({ compact = false }) {
  const [lightbox, setLightbox] = useState(null);
  const navigate = useNavigate();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32 px-5 sm:px-8 bg-[#0c0c0c] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#9DEB30] mb-3">
              · Nos services
            </div>
            <h2
              data-testid="services-title"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
            >
              Nettoyage technique &<br />
              <span className="text-[#9DEB30]">signalisation innovante.</span>
            </h2>
          </div>
          {!compact && (
            <Link
              to="/services"
              data-testid="services-view-all"
              className="self-start md:self-end inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-[#9DEB30] transition"
            >
              Voir tous les services
              <ArrowUpRight size={16} />
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const slug = SERVICE_SLUG_MAP[s.id];
            const goToDetail = () => slug && navigate(`/${slug}`);
            return (
              <article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                onClick={goToDetail}
                role={slug ? "link" : undefined}
                tabIndex={slug ? 0 : -1}
                onKeyDown={(e) => {
                  if (slug && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    goToDetail();
                  }
                }}
                className={`group relative neon-border rounded-2xl overflow-hidden bg-[#101010] ${slug ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#9DEB30]/60" : ""}`}
              >
                <div className="grid grid-cols-1 sm:grid-cols-5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightbox({ image: s.image, title: s.title, hideBottomWatermark: s.hideBottomWatermark });
                    }}
                    data-testid={`service-image-${s.id}`}
                    aria-label={`Agrandir l'image — ${s.title}`}
                    className="sm:col-span-2 relative aspect-[4/3] sm:aspect-auto overflow-hidden text-left cursor-zoom-in group/img"
                  >
                    <img
                      src={s.image}
                      alt={s.title}
                      style={{ objectPosition: s.imagePosition || "center" }}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/30 to-transparent" />
                    <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider bg-black/70 border border-[#9DEB30]/30 text-[#9DEB30]">
                      {s.tag}
                    </div>
                    <div className="absolute bottom-4 right-4 h-9 w-9 rounded-full bg-black/70 border border-white/15 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover/img:opacity-100 transition">
                      <ZoomIn size={16} />
                    </div>
                  </button>

                  <div className="sm:col-span-3 p-7 flex flex-col">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-[#9DEB30]/10 border border-[#9DEB30]/30 text-[#9DEB30]">
                        <Icon size={16} />
                      </div>
                      <span className="text-xs font-mono text-zinc-500">
                        Service · 0{i + 1}
                      </span>
                    </div>

                    <h3 className="font-display mt-4 text-2xl font-bold leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-zinc-400">{s.short}</p>

                    <ul className="mt-4 space-y-2">
                      {s.bullets.map((b, j) => (
                        <li
                          key={j}
                          className="flex items-start gap-2 text-sm text-zinc-300"
                        >
                          <Check
                            size={14}
                            className="text-[#9DEB30] mt-1 shrink-0"
                          />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-6 flex items-center gap-4 flex-wrap">
                      {slug && (
                        <span
                          data-testid={`service-learn-${s.id}`}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-white group-hover:text-[#9DEB30]"
                        >
                          En savoir plus
                          <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </span>
                      )}
                      <Link
                        to="/contact"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`service-cta-${s.id}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#9DEB30] hover:underline underline-offset-4"
                      >
                        Demander un devis
                        <ArrowUpRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {lightbox && (
        <div
          data-testid="service-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10 animate-in fade-in"
        >
          <button
            data-testid="service-lightbox-close"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Fermer"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 h-11 w-11 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-white flex items-center justify-center transition"
          >
            <X size={20} />
          </button>
          <figure
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-6xl w-full max-h-full"
          >
            <img
              src={lightbox.image}
              alt={lightbox.title}
              style={lightbox.hideBottomWatermark ? { clipPath: "inset(0 0 8% 0)" } : undefined}
              className="w-full max-h-[85vh] object-contain rounded-xl"
            />
            <figcaption className="mt-4 text-center text-sm text-zinc-300 font-mono">
              {lightbox.title}
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}
